/**
 * Menu Configuration
 *
 * Data-driven menu structure for the navigation drawer.
 * This replaces the repetitive JSX in NavList.jsx with a declarative configuration.
 *
 * Each category has:
 * - key: Unique identifier used for state management
 * - titleKey: Translation key
 * - icon: MUI icon component name
 * - condition: Optional condition for visibility (maps to auth.user properties)
 * - items: Array of menu items
 * - subCategories: Optional array of nested sub-categories
 * - subCategoriesFirst: If true, render subCategories before items (default: false, items first)
 *
 * Each item has:
 * - key: Used to check availableMenu.options
 * - path: Navigation route
 * - index: Used to check selection state (menuState.index)
 * - titleKey: Translation key
 * - screenDef: Key in screenDefs object
 * - condition: Optional condition (maps to auth.user properties)
 * - negativeCondition: If true, show when condition is FALSE
 */

// Icon imports - these will be used by the rendering component
// The config stores icon names as strings to keep this file serializable
export const ICONS = {
  DashMenuIcon: 'DashMenuIcon',
  CultivationIcon: 'CultivationIcon',
  ProcessingIcon: 'ProcessingIcon',
  FacilityIcon: 'FacilityIcon',
  InventoryIcon: 'InventoryIcon',
  QAIcon: 'QAIcon',
  CRMIcon: 'CRMIcon',
  ReportingIcon: 'ReportingIcon',
  ProductionIcon: 'ProductionIcon',
  DocumentsIcon: 'DocumentsIcon',
  SettingsIcon: 'SettingsIcon',
  SecurityIcon: 'SecurityIcon',
  TimelapseTwoToneIcon: 'TimelapseTwoToneIcon',
  InvestigationsIcon: 'InvestigationsIcon',
};

/**
 * Standalone menu items (no category)
 */
export const STANDALONE_ITEMS = [
  {
    key: 'launchPad',
    category: 'launchPad',
    path: '/#/launchPad',
    index: 'launchPad',
    titleKey: 'launchPad',
    screenDef: 'LaunchPad',
    condition: { type: 'hideLaunchPad', negate: true },
  },
  {
    key: 'home',
    category: 'home',
    path: '/#/home',
    index: 'home',
    titleKey: 'home',
    screenDef: 'Home',
    resetNav: true,
  },
  {
    key: 'dashboards',
    category: 'dashboards',
    path: '/#/dashboards',
    index: 'dashboards',
    titleKey: 'dashboards',
    screenDef: 'Dashboards',
    resetNav: true,
  },
  {
    key: 'scheduledActivities',
    category: 'scheduledActivities',
    path: '/#/scheduledActivities',
    index: 'scheduledActivities',
    titleKey: 'schedule',
    screenDef: 'ScheduledActivities',
  },
  {
    key: 'admin',
    category: 'admin',
    path: '/#/settings',
    index: 'settings',
    titleKey: 'settings',
    screenDef: 'AdminSettings',
    icon: ICONS.SettingsIcon,
  },
];

/**
 * Main menu categories configuration
 */
export const MENU_CATEGORIES = [
  // ==========================================
  // DASHBOARDS
  // ==========================================
  // {
  //   key: 'dashboard',
  //   titleKey: 'dashboards',
  //   icon: ICONS.DashMenuIcon,
  //   items: [
  //     { key: 'CustomDashboards', path: '/#/dashboards', index: 'customDashboards', titleKey: 'customDashboards', screenDef: 'CustomDashboards' },
  //   ],
  // },

  // ==========================================
  // CULTIVATION
  // ==========================================
  {
    key: 'cultivation',
    titleKey: 'cultivation',
    icon: ICONS.CultivationIcon,
    condition: 'cultivator',
    items: [
      { key: 'CultivationHub', path: '/#/cultivationhub', index: 'cultivationhub', titleKey: 'cultivationHub', screenDef: 'CultivationHub', dataCy: 'cultivationHub' },
      { key: 'CultivationBatchList', path: '/#/cultivationbatches', index: 'cultivationbatch', titleKey: 'batches', screenDef: 'CultivationBatchList', dataCy: 'cultivationBatches' },
      { key: 'BatchActivities', path: '/#/batchactivities', index: 'batchActivities', titleKey: 'batchActivities', screenDef: 'BatchActivities', dataCy: 'batchActivities' },
      { key: 'RoomFeedings', path: '/#/roomfeedings', index: 'roomFeedings', titleKey: 'roomFeedings', screenDef: 'RoomFeeding', condition: { type: 'isMushrooms', negate: true } },
      { key: 'FeedingLotList', path: '/#/feedinglots', index: 'feedinglots', titleKey: 'feedingLots', screenDef: 'FeedingLotList', condition: { type: 'isMushrooms', negate: true } },
      { key: 'FeedingRecipes', path: '/#/feedingrecipes', index: 'feedRecipes', titleKey: 'feedingRecipes', screenDef: 'FeedingRecipe', condition: { type: 'isMushrooms', negate: true } },
      { key: 'Strains', path: '/#/strains', index: 'strains', titleKey: 'strains', screenDef: 'Strains' },
      { key: 'IPMTreatmentList', path: '/#/croptreatments', index: 'ipmTreatments', titleKey: 'cropTreatments', screenDef: 'IPMTreatmentList', condition: { type: 'isMushrooms', negate: true } },
      { key: 'IPMSolutionList', path: '/#/pesticidepreps', index: 'ipmSolutions', titleKey: 'pesticideSolution', screenDef: 'IPMSolutionList', condition: { type: 'isMushrooms', negate: true } },
      { key: 'PesticideRecipes', path: '/#/pesticiderecipes', index: 'pesticideRecipes', titleKey: 'pesticideRecipes', screenDef: 'PesticideRecipes', condition: { type: 'isMushrooms', negate: true } },
      { key: 'BatchTemplates', path: '/#/batchtemplates', index: 'batchtemplates', titleKey: 'templates', screenDef: 'BatchTemplates' },
      { key: 'BatchActivityDefinitions', path: '/#/batchactivitydefinitions', index: 'batchactivitydefinitions', titleKey: 'activityDefinitions', screenDef: 'BatchActivityDefinitions' },
    ],
  },

  // ==========================================
  // PROCESSING
  // ==========================================
  {
    key: 'processing',
    titleKey: 'processing',
    icon: ICONS.ProcessingIcon,
    items: [
      { key: 'ProcessingHub', path: '/#/processinghub', index: 'processinghub', titleKey: 'processingHub', screenDef: 'ProcessingHub', dataCy: 'processingHub' },
      { key: 'ProductionFloorHub', path: '/#/productionfloorhub', index: 'productionfloorhub', titleKey: 'productionFloorHub', screenDef: 'ProductionFloorHub', dataCy: 'productionFloorHub' },
      { key: 'PlannerHub', path: '/#/plannerhub', index: 'plannerhub', titleKey: 'plannerHub', screenDef: 'PlannerHub', dataCy: 'plannerHub' },
      { key: 'TrimmingOrderList', path: '/#/trimmingorders', index: 'trimmings', titleKey: 'trimmings', screenDef: 'TrimmingOrderList' },
      { key: 'DryingOrderList', path: '/#/dryingorders', index: 'drying', titleKey: 'dryings', screenDef: 'DryingOrderList', condition: { type: 'isMushrooms', negate: true } },
      { key: 'CuringList', path: '/#/curings', index: 'curings', titleKey: 'curings', screenDef: 'CuringList', condition: { type: 'isMushrooms', negate: true } },
      { key: 'ProductionBatches', path: '/#/productionbatches', index: 'productionBatches', titleKey: 'batches', screenDef: 'ProductionBatches' },
      { key: 'ProductionOrderList', path: '/#/eproductionorders', index: 'productionorders', titleKey: 'productionOrders', screenDef: 'ProductionOrderList' },
      { key: 'MasterCaseList', path: '/#/mastercases', index: 'masterCasing', titleKey: 'masterCases', screenDef: 'MasterCaseList' },
      { key: 'Routings', path: '/#/routings', index: 'routings', titleKey: 'routings', screenDef: 'Routings' },
      { key: 'ProductionBoms', path: '/#/boms', index: 'boms', titleKey: 'productionBoms', screenDef: 'ProductionBoms' },
    ],
  },

  // ==========================================
  // FACILITY
  // ==========================================
  {
    key: 'facility',
    titleKey: 'facility',
    icon: ICONS.FacilityIcon,
    items: [
      { key: 'DestructionList', path: '/#/destructions', index: 'destructions', titleKey: 'destructions', screenDef: 'DestructionList' },
      { key: 'Rooms', path: '/#/rooms', index: 'rooms', titleKey: 'rooms', screenDef: 'Rooms' },
      { key: 'RoomCheckList', path: '/#/roomchecklist', index: 'roomchecklist', titleKey: 'roomChecks', screenDef: 'RoomCheckList' },
      { key: 'RoomCleaningList', path: '/#/roomcleanings', index: 'roomcleanings', titleKey: 'roomCleanings', screenDef: 'RoomCleaningList' },
      { key: 'EquipmentList', path: '/#/equipmentlist', index: 'equipmentlist', titleKey: 'equipment', screenDef: 'EquipmentList' },
      { key: 'EquipmentCalibrationChecks', path: '/#/equipmentcalibrationchecks', index: 'equipmentCalibrationChecks', titleKey: 'calibrationChecks', screenDef: 'EquipmentCalibrationChecks' },
      { key: 'EquipmentCalibrationTypes', path: '/#/equipmentcalibrationtypes', index: 'equipmentCalibrationTypes', titleKey: 'calibrationTypes', screenDef: 'EquipmentCalibrationTypes' },
      { key: 'EquipmentLogs', path: '/#/equipmentlogs', index: 'equipmentlogs', titleKey: 'maintenanceLogs', screenDef: 'EquipmentLogs' },
      { key: 'BinList', path: '/#/bins', index: 'bins', titleKey: 'bins', screenDef: 'BinList' },
      { key: 'LocationList', path: '/#/locations', index: 'locations', titleKey: 'locations', screenDef: 'LocationList' },
      { key: 'MaintenanceRequestList', path: '/#/maintenancerequests', index: 'maintenancerequests', titleKey: 'serviceRequests', screenDef: 'MaintenanceRequestList' },
      { key: 'CleaningSolutionList', path: '/#/cleaningsolutions', index: 'cleaningsolutions', titleKey: 'cleaningSolutions', screenDef: 'CleaningSolutionList' },
      { key: 'CleaningSolutionRecipes', path: '/#/cleaningsolutionrecipes', index: 'cleaningSolutionRecipes', titleKey: 'cleaningSolutionRecipes', screenDef: 'CleaningSolutionRecipes' },
      { key: 'PestScoutingList', path: '/#/pestscoutings', index: 'pestscoutings', titleKey: 'pestScoutings', screenDef: 'PestScoutingList' },
      { key: 'Teams', path: '/#/teams', index: 'teams', titleKey: 'teams', screenDef: 'Teams' },
      { key: 'LocationAudits', path: '/#/locationaudits', index: 'locationaudits', titleKey: 'locationAudits', screenDef: 'LocationAudits' }
    ],
    // Nested sub-categories under Facility
    subCategories: [
      {
        key: 'security',
        titleKey: 'security',
        icon: ICONS.SecurityIcon,
        items: [
          { key: 'SigninList', path: '/#/signins', index: 'signins', titleKey: 'signins', screenDef: 'SigninList' },
          { key: 'VisitorList', path: '/#/visitors', index: 'visitors', titleKey: 'visitors', screenDef: 'VisitorList' },
        ],
      },
    ],
  },

  // ==========================================
  // INVENTORY
  // ==========================================
  {
    key: 'inventory',
    titleKey: 'inventory',
    icon: ICONS.InventoryIcon,
    items: [
      { key: 'ItemList', path: '/#/itemlist', index: 'itemlist', titleKey: 'items', screenDef: 'ItemList' },
      { key: 'LotList', path: '/#/lots', index: 'lots', titleKey: 'lots', screenDef: 'LotList' },
      { key: 'ContainerList', path: '/#/containers', index: 'containers', titleKey: 'containers', screenDef: 'ContainerList' },
      { key: 'Strains', path: '/#/strains', index: 'strains', titleKey: 'strains', screenDef: 'Strains', condition: { type: 'cultivator', negate: true } },
      { key: 'ItemLedgers', path: '/#/itemledgers', index: 'itemledgers', titleKey: 'itemLedgers', screenDef: 'ItemLedgers' },
      { key: 'GTINs', path: '/#/gtins', index: 'gtins', titleKey: 'gtins', screenDef: 'GTINs' },
      { key: 'ExciseAdjustmentList', path: '/#/exciseadjustments', index: 'ExciseAdjustments', titleKey: 'exciseAdjustments', screenDef: 'ExciseAdjustmentList' },
      { key: 'DestructionReport', path: '/#/destructionreport', index: 'destructionReport', titleKey: 'destructionReport', screenDef: 'DestructionReport' },
      { key: 'VendorList', path: '/#/vendors', index: 'vendors', titleKey: 'vendors', screenDef: 'VendorList' },
      { key: 'ShipmentList', path: '/#/shipments', index: 'shipments', titleKey: 'shipments', screenDef: 'ShipmentList' },
      { key: 'ReceiptList', path: '/#/receipts', index: 'receipts', titleKey: 'receipts', screenDef: 'ReceiptList' },
      { key: 'PurchaseOrderList', path: '/#/purchaseorders', index: 'purchaseOrders', titleKey: 'purchaseOrders', screenDef: 'PurchaseOrderList' },
      { key: 'ItemCategories', path: '/#/itemcategories', index: 'itemcategories', titleKey: 'itemCategories', screenDef: 'ItemCategories' },
      { key: 'PastInventoryList', path: '/#/pastinventories', index: 'PastInventoryList', titleKey: 'pastInventory', screenDef: 'PastInventoryList' },
      { key: 'BinSummary', path: '/#/binsummary', index: 'binSummary', titleKey: 'binSummary', screenDef: 'BinSummary' },
    ],
  },

  // ==========================================
  // QUALITY
  // ==========================================
  {
    key: 'quality',
    titleKey: 'quality',
    icon: ICONS.QAIcon,
    subCategoriesFirst: true, // Show Testing and Investigations before single items
    items: [
      { key: 'ExternalTreatmentList', path: '/#/exttreats', index: 'externaltreatments', titleKey: 'externalTreatments', screenDef: 'ExternalTreatmentList' },
      { key: 'RiskAssessments', path: '/#/riskassessments', index: 'riskassessments', titleKey: 'riskAssessments', screenDef: 'RiskAssessments' },
    ],
    subCategories: [
      {
        key: 'tests',
        titleKey: 'testing',
        icon: ICONS.TimelapseTwoToneIcon,
        items: [
          { key: 'TestResultList', path: '/#/testresults', index: 'testresultlist', titleKey: 'testResults', screenDef: 'TestResultList' },
          { key: 'TestDefinitionList', path: '/#/testdefinitions', index: 'testdefinitions', titleKey: 'testDefinitions', screenDef: 'TestDefinitionList' },
          { key: 'TestItemList', path: '/#/testitems', index: 'testItems', titleKey: 'testItems', screenDef: 'TestItemList' },
          { key: 'TestItemCategories', path: '/#/testitemcategories', index: 'testItemCategories', titleKey: 'testItemCategories', screenDef: 'TestItemCategories' },
        ],
      },
      {
        key: 'investigations',
        titleKey: 'investigations',
        icon: ICONS.InvestigationsIcon,
        items: [
          { key: 'CapaList', path: '/#/capas', index: 'capas', titleKey: 'capas', screenDef: 'CapaList' },
          { key: 'DeviationList', path: '/#/deviations', index: 'deviations', titleKey: 'deviations', screenDef: 'DeviationList' },
          { key: 'NcrList', path: '/#/ncrs', index: 'ncrs', titleKey: 'ncrs', screenDef: 'NcrList' },
          { key: 'LossCannabisList', path: '/#/losses', index: 'losses', titleKey: 'lossOrTheft', screenDef: 'LossCannabisList' },
          { key: 'CorrectiveActionRequests', path: '/#/cars', index: 'correctiveActionRequests', titleKey: 'cars', screenDef: 'CorrectiveActionRequests' },
          { key: 'EmployeeIllnessList', path: '/#/illnesses', index: 'illnesses', titleKey: 'employeeIllnesses', screenDef: 'EmployeeIllnessList' },
          { key: 'IncidentList', path: '/#/majors', index: 'majors', titleKey: 'incidents', screenDef: 'IncidentList' },
          { key: 'RecallList', path: '/#/recalls', index: 'recalls', titleKey: 'recalls', screenDef: 'RecallList' },
          { key: 'VendorAuditList', path: '/#/vendoraudits', index: 'vendoraudits', titleKey: 'vendorAudits', screenDef: 'VendorAuditList' }
        ],
      },
    ],
  },

  // ==========================================
  // CRM
  // ==========================================
  {
    key: 'crm',
    titleKey: 'crm',
    icon: ICONS.CRMIcon,
    items: [
      { key: 'SalesHub', path: '/#/saleshub', index: 'saleshub', titleKey: 'salesHub', screenDef: 'SalesHub', dataCy: 'salesHub' },
      { key: 'CustomerList', path: '/#/customers', index: 'customers', titleKey: 'customers', screenDef: 'CustomerList' },
      { key: 'SalesList', path: '/#/sales', index: 'sales', titleKey: 'sales', screenDef: 'SalesList' },
      { key: 'ConsignmentSales', path: '/#/consignmentsales', index: 'consignmentsales', titleKey: 'consignmentSales', screenDef: 'ConsignmentSales' },
      { key: 'ContractSales', path: '/#/contractsales', index: 'contractsales', titleKey: 'contractSales', screenDef: 'ContractSales' },
      { key: 'CCXPostings', path: '/#/ccxpostings', index: 'CcxPostings', titleKey: 'ccxPostings', screenDef: 'CCXPostings', condition: 'ccx' },
      { key: 'InvoiceList', path: '/#/invoices', index: 'invoices', titleKey: 'invoices', screenDef: 'InvoiceList' },
      { key: 'CustomerComplaints', path: '/#/customercomplaints', index: 'customerComplaints', titleKey: 'customerComplaints', screenDef: 'CustomerComplaints' },
      { key: 'MarketMap', path: '/#/marketmap', index: 'marketmap', titleKey: 'marketMap', screenDef: 'MarketMap', condition: 'retail' },
    ],
  },

  // ==========================================
  // REPORTING
  // ==========================================
  {
    key: 'reporting',
    titleKey: 'reporting',
    icon: ICONS.ReportingIcon,
    items: [
      { key: 'Performance', path: '/#/performance', index: 'performance', titleKey: 'performanceDashboard', screenDef: 'Performance' },
      { key: 'HCReportList', path: '/#/hcreports', index: 'hcreports', titleKey: 'hcReports', screenDef: 'HCReportList' },
      { key: 'AvailableInventoryList', path: '/#/availableinventory', index: 'availableInventory', titleKey: 'availableInventory', screenDef: 'AvailableInventoryList' },
      { key: 'ProductionTotals', path: '/#/productiontotals', index: 'productionTotals', titleKey: 'productionTotals', screenDef: 'ProductionTotals' },
      { key: 'AnnualRevenueReports', path: '/#/annualrevenuereports', index: 'annualRevenueReports', titleKey: 'annualRevenueReports', screenDef: 'AnnualRevenueReports' },
      { key: 'ExciseStampAudit', path: '/#/excisestampaudit', index: 'excisestampaudit', titleKey: 'exciseStampAudit', screenDef: 'ExciseStampAudit' },
      { key: 'SaskatchewanReportList', path: '/#/saskatchewanreports', index: 'saskatchewanreports', titleKey: 'saskatchewanReports', screenDef: 'SaskatchewanReportList' },
    ],
  },

  // ==========================================
  // PRODUCTION
  // ==========================================
  {
    key: 'production',
    titleKey: 'production',
    icon: ICONS.ProductionIcon,
    items: [
      { key: 'ProductionPlanList', path: '/#/productionplans', index: 'productionplans', titleKey: 'productionPlans', screenDef: 'ProductionPlanList' },
      { key: 'ProductionScheduleList', path: '/#/productionschedules', index: 'productionschedules', titleKey: 'productionSchedules', screenDef: 'ProductionScheduleList' },
    ],
  },

  // ==========================================
  // DOCUMENTS
  // ==========================================
  {
    key: 'documents',
    titleKey: 'documents',
    icon: ICONS.DocumentsIcon,
    condition: { type: 'micro', negate: true },
    items: [
      { key: 'FileManager', path: '/#/filemanager', index: 'filemanager', titleKey: 'fileManager', screenDef: 'FileManager' },
      { key: 'ManagedDocumentList', path: '/#/manageddocs', index: 'manageddocs', titleKey: 'documents', screenDef: 'ManagedDocumentList' },
      { key: 'DocumentReviewList', path: '/#/manageddocreviews', index: 'manageddocreviews', titleKey: 'documentReviews', screenDef: 'DocumentReviewList' },
      { key: 'ChangeRequestList', path: '/#/changerequests', index: 'changerequests', titleKey: 'changeRequests', screenDef: 'ChangeRequestList' },
      { key: 'SopList', path: '/#/sops', index: 'sops', titleKey: 'formTemplates', screenDef: 'SopList' },
      { key: 'Forms', path: '/#/forms', index: 'forms', titleKey: 'forms', screenDef: 'Forms' },
      // Training records, group training and training analytics live under Human Resources.
      { key: 'ManagedDocumentTemplates', path: '/#/manageddocumenttemplates', index: 'manageddocumenttemplates', titleKey: 'documentTemplates', screenDef: 'ManagedDocumentTemplates' },
    ],
  },

  // ==========================================
  // HUMAN RESOURCES
  // ==========================================
  // The category key must match the screen category label the server returns in
  // availableMenu.categories ('hrmanagement' — see the hrManagement screen category), otherwise
  // the whole section is filtered out before it is ever rendered.
  {
    key: 'hrmanagement',
    titleKey: 'humanResources',
    icon: ICONS.CRMIcon,
    items: [
      // The hub is the section's landing page, so it leads.
      { key: 'DocumentAnalytics', path: '/#/documentanalytics', index: 'documentanalytics', titleKey: 'trainingHub', screenDef: 'DocumentAnalytics' },
      { key: 'ManageUsers', path: '/#/manageusers', index: 'manageusers', titleKey: 'users', screenDef: 'ManageUsers' },
      // Training has one home. Documents keeps the controlled documents themselves; who is
      // trained on them, and printing that off, belongs to the supervisor's section.
      { key: 'DocumentCertificationList', path: '/#/certs', index: 'documentCertifications', titleKey: 'trainingRecords', screenDef: 'DocumentCertificationList' },
      { key: 'GroupTrainingSessions', path: '/#/grouptrainings', index: 'grouptrainings', titleKey: 'groupTrainingSessions', screenDef: 'GroupTrainingSessions' },
      { key: 'AttendanceAnalytics', path: '/#/attendanceanalytics', index: 'attendanceanalytics', titleKey: 'attendanceAnalytics', screenDef: 'AttendanceAnalytics' },
    ],
  },

];

/**
 * Internal Admin only items (shown when isInternalAdmin is true)
 */
export const INTERNAL_ADMIN_ITEMS = [
  { key: 'HealthDashboard', path: '/#/healthdashboard', index: 'healthdashboard', titleKey: 'systemHealth', screenDef: 'HealthDashboard', icon: 'HealthDashboardIcon' },
  { key: 'ComplianceEvidence', path: '/#/complianceevidence', index: 'complianceevidence', titleKey: 'complianceEvidence', screenDef: 'ComplianceEvidence', icon: 'ComplianceEvidenceIcon' },
  { key: 'Announcements', path: '/#/announcements', index: 'announcements', titleKey: 'announcements', screenDef: 'Announcements', icon: 'AnnouncementNavIcon' },
  { key: 'EmailTemplates', path: '/#/emailtemplates', index: 'emailtemplates', titleKey: 'emailTemplates', screenDef: 'EmailTemplates', icon: 'EmailTemplatesIcon' },
  { key: 'EmailsSent', path: '/#/emailssent', index: 'emailssent', titleKey: 'emailsSent', screenDef: 'EmailsSent', icon: 'EmailsSentIcon' },
  { key: 'HelpLinks', path: '/#/helplinks', index: 'helplinks', titleKey: 'helpLinks', screenDef: 'HelpLinks', icon: 'HelpLibraryIcon' },
  { key: 'GuideAdministration', path: '/#/guides', index: 'guides', titleKey: 'guideAdministration', screenDef: 'GuideAdministration', icon: 'HelpLibraryIcon' },
];

/**
 * Global admin items (available to all admin users)
 */
export const GLOBAL_ITEMS = [
  { key: 'DataUpload', path: '/#/datauploads', index: 'dataupload', titleKey: 'dataUpload', screenDef: 'DataUpload', icon: 'DataUploadIcon' },
  { key: 'Dispensaries', path: '/#/dispensaries', index: 'dispensaries', titleKey: 'dispensaries', screenDef: 'Dispensaries', icon: 'DispensaryIcon' },
  { key: 'AdminLogs', path: '/#/logs', index: 'adminlogs', titleKey: 'adminLogs', screenDef: 'AdminLogs', internalOnly: true, icon: 'AdminLogsIcon' },
  { key: 'Management', path: '/#/management', index: 'management', titleKey: 'management', screenDef: 'Management', internalOnly: true, icon: 'ManagementIcon' },
  { key: 'Provinces', path: '/#/provinces', index: 'provinces', titleKey: 'provinces', screenDef: 'Provinces', internalOnly: true },
  { key: 'Countries', path: '/#/countries', index: 'countries', titleKey: 'countries', screenDef: 'Countries', internalOnly: true },
  { key: 'Objects', path: '/#/objects', index: 'objects', titleKey: 'objects', screenDef: 'Objects', internalOnly: true },
  { key: 'GlobalObjects', path: '/#/globalobjects', index: 'globalobjects', titleKey: 'globalObjects', screenDef: 'GlobalObjects', internalOnly: true },
  { key: 'QueueJobs', path: '/#/queuejobs', index: 'queuejobs', titleKey: 'queueJobs', screenDef: 'QueueJobs', internalOnly: true, icon: 'QueueJobsIcon' },
  { key: 'ScreenDefinitions', path: '/#/screendefinitions', index: 'screendefinitions', titleKey: 'screenDefinitions', screenDef: 'ScreenDefinitions', internalOnly: true, icon: 'ScreenDefsIcon' },
];

/**
 * Exit tenant item (for internal users with tenant context)
 */
export const EXIT_TENANT = {
  key: 'exitTenant',
  titleKey: 'exitTenant',
  action: 'exitTenant',
};

/**
 * Helper function to check if an item should be visible based on conditions
 * @param {Object} item - Menu item with potential condition
 * @param {Object} auth - Auth state from Redux
 * @returns {boolean} Whether the item should be visible
 */
export const shouldShowItem = (item, auth) => {
  if (!item.condition) return true;

  if (typeof item.condition === 'string') {
    // Simple condition: check auth.user[condition]
    return auth?.user?.[item.condition] === true;
  }

  if (typeof item.condition === 'object') {
    const { type, negate } = item.condition;
    const value = auth?.user?.[type] === true;
    return negate ? !value : value;
  }

  return true;
};

/**
 * Helper to check if category should be visible
 * @param {Object} category - Category config
 * @param {Object} availableMenu - Available menu from security
 * @param {Object} auth - Auth state
 * @returns {boolean}
 */
export const shouldShowCategory = (category, availableMenu, auth) => {
  // Check if category is in availableMenu.categories
  if (!availableMenu?.categories?.includes(category.key)) {
    return false;
  }

  // Check condition if present
  return shouldShowItem(category, auth);
};

export default MENU_CATEGORIES;
